import { useEffect, useState } from "react";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { getSocket, onSocketEvent } from "../auth/AuthContext/socketManager";
import { useAuth } from "../auth/AuthContext/AuthContext";

interface ServiceMessage {
  id: string;
  message: string;
  sentAt: string;
  sender: string;
  sessionId?: string;
}

interface ReadResult {
  ok: boolean;
  error?: string;
}

const ServiceMessageNotice = () => {
  const { isAuthenticated } = useAuth();
  const [messages, setMessages] = useState<ServiceMessage[]>([]);
  const [reading, setReading] = useState(false);
  const [readError, setReadError] = useState("");

  useEffect(() => {
    if (!isAuthenticated) {
      setMessages([]);
      setReading(false);
      setReadError("");
      return;
    }
    return onSocketEvent("service-message:receive", (notice: ServiceMessage) => {
    if (notice && typeof notice.id === "string" && typeof notice.message === "string" && notice.message.trim()) {
      setMessages((current) => current.some(item => item.id === notice.id)
        ? current : [...current, { ...notice, sessionId: getSocket()?.id }]);
    }
    });
  }, [isAuthenticated]);

  const current = messages[0];
  const acknowledgeAndClose = () => {
    if (!current) return;
    const socket = getSocket();
    // A reconnected socket represents a new session. The old session is no
    // longer monitored; close its notice locally without claiming server receipt.
    if (current.sessionId && socket?.connected && current.sessionId !== socket.id) {
      setMessages((items) => items.slice(1));
      setReadError("");
      return;
    }
    if (!socket?.connected) {
      setReadError("Connessione in tempo reale non disponibile. Riprova quando torna attiva.");
      return;
    }
    setReading(true);
    setReadError("");
    const messageId = current.id;
    socket.timeout(5000).emit("service-message:read", messageId, (error: Error | null, result: ReadResult) => {
      setReading(false);
      if (error || !result?.ok) {
        setReadError(result?.error || "Conferma non ricevuta dal server. Riprova.");
        return;
      }
      setMessages((items) => items[0]?.id === messageId ? items.slice(1) : items);
    });
  };
  return (
    <Dialog open={Boolean(current)} onClose={() => { if (!reading) { setMessages((items) => items.slice(1)); setReadError(""); } }} fullWidth maxWidth="sm" aria-labelledby="service-message-title">
      <DialogTitle id="service-message-title">Avviso di servizio</DialogTitle>
      <DialogContent>
        <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{current?.message}</Typography>
        {current && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {current.sender ? `Da ${current.sender} · ` : ""}
              {new Date(current.sentAt).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}
            </Typography>
          </Box>
        )}
        {readError && <Alert severity="error" sx={{ mt: 2 }}>{readError}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={acknowledgeAndClose} disabled={reading} autoFocus>{reading ? "Confermo…" : "Ho letto"}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceMessageNotice;
